import { Component, computed, inject, signal } from '@angular/core';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { User } from '../../../api/app-types/user.app';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { ContactType } from '../../../api/app-types/contact-type';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { Contact, ContactParticipant, UpdateContactBody } from '../../../api/app-types/contact.app';
import { LvrHeading } from '../../../core/components/heading/heading';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DocumentFile, DocumentFileSubjectType } from '../../../api/app-types/document.app';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { CONTACT_API } from '../../../api/services/contact.service';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { formatDateToISOString, matDateProviders } from '../../../shared/helpers/date.func';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { maxDateValidator, minDateValidator } from '../../../shared/validators/max-date-validator';
import { SearchResultItem } from '../../../api/app-types/search.app';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../shared/document-upload/document-upload-validators';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrField } from '../../../core/components/field/field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { Participants } from '../../contact/participants/participants';
import { DocumentUploadControl } from '../../../shared/document-upload/document-upload';
import { DatePipe } from '@angular/common';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { hasError, hasFileError } from '../../../shared/helpers/has-error.func';
import { compareById } from '../../../shared/helpers/compare-by-id.func';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { hasPermission } from '../../../shared/permissions/permission.func';

@Component({
	selector: 'lvr-network-contact-edit',
	imports: [
		TranslocoPipe,
		LvrHeading,
		LvrPaddingDirective,
		LvrAppendAsteriskPipe,
		LvrField,
		LvrInputDirective,
		LvrDefaultActions,
		LvrRestoreActions,
		Participants,
		DocumentUploadControl,
		DatePipe,
		ReactiveFormsModule,
		LvrAutoSaveDirective,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatCheckboxModule,
		MatSelectModule,
	],
	providers: [matDateProviders],
	templateUrl: './network-contact-edit.html',
	styleUrl: './network-contact-edit.scss',
})
export class NetworkContactEdit extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly api = inject(CONTACT_API);
	private readonly ntfySvc = inject(NOTIFICATION_SERVICE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);

	private readonly contactTypesPage = this.resolveDataAsSignal<PaginatedResponse<ContactType>>('contactTypes');
	public readonly user = this.resolveDataAsSignal<User>('user');
	public readonly contact = this.resolveDataAsSignal<Contact>('contact');

	public readonly contactTypes = computed(() => this.contactTypesPage()?.entities);

	public readonly isLoading = signal<boolean>(false);
	public readonly draftId = computed(() => `${FORM_KEYS.EDIT_NETWORK_CONTACT}.${this.contact()?.id}`);
	public readonly canEditContactsPermission = hasPermission(this.session.permissions, 'Contacts.Edit');
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly compareById = compareById;

	public readonly dateRange = {
		min: this.env.formConfiguration.contact.dateWarningRange.min,
		max: this.env.formConfiguration.contact.dateWarningRange.max, // days retroactively
	};

	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly remarkCtrlMaxLength = this.env.textareaMaxLength;
	public readonly keywordCtrlMaxLength = this.env.textInputMaxLength;

	public readonly subjectType = DocumentFileSubjectType.CONTACT;
	public readonly maxFileCount = this.env.contactSettings.maxFiles;
	public readonly maxFileSize = this.env.fileSettings.maxFileSize;
	public readonly allowedFileTypes = this.env.fileSettings.allowedMimeTypes;

	public readonly minDate = computed(() => {
		const minusDays = this.dateRange.max; // 42
		if (!minusDays) return undefined;

		const today = new Date();
		const d = new Date(today);

		d.setDate(d.getDate() - minusDays);
		d.setHours(0, 0, 0, 0);

		return d;
	});

	public readonly maxDate = computed(() => {
		const plusDays = this.dateRange.min ?? 0;

		const d = new Date();
		d.setDate(d.getDate() + plusDays);
		d.setHours(23, 59, 59, 999);

		return d;
	});

	public readonly form = this.initForm(inject(FormBuilder));

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);
	public hasFileError = (control: AbstractControl | null, ...errors: string[]) => hasFileError(control, ...errors);

	public cancel = async () => {
		this.draftService.deleteDraft('contact', this.draftId());
		await this.router.navigate(['../'], { relativeTo: this.route });
	};

	public async save() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const contact = this.contact();
		if (!contact) return;

		const fv = this.form.value;
		const docs = (fv.documents ?? []) as DocumentFile[];

		const body: UpdateContactBody = {
			id: contact.id,
			anonym: fv.anonym ?? false,
			confidential: fv.confidential ?? false,
			contactedAt: formatDateToISOString(fv.contactedAt) ?? '',
			contactType: fv.contactType!,
			keyword: fv.keyword?.trim() ?? '',
			remark: fv.remark?.trim() ?? '',
			participants: (fv.participants ?? []).map((p: SearchResultItem) => ({
				id: p.item.id,
				typeId: p.personType,
			})),
			documentIds: docs.map((d: DocumentFile) => d.id),
			selectedTasksIds: [],
		};

		this.isLoading.set(true);
		try {
			await this.api.updateNetworkContact(contact.id, body);
			this.ntfySvc.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.network_contact_edited')),
			);
			await this.cancel();
		} catch {
			this.ntfySvc.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.network_contact_edit_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	private initForm(fb: FormBuilder) {
		const contact = this.contact();
		const participants = (contact?.participants ?? []).map(
			(p: ContactParticipant): SearchResultItem => ({
				item: {
					id: p.item.id,
					name: p.item.name,
					firstName: p.item.firstName,
					objectId: '',
				},
				personType: p.personTypeId,
				contactPoints: p.contactPoints,
			}),
		);

		return fb.group({
			anonym: fb.control<boolean>(contact?.anonym ?? false),
			remark: fb.control<string>(contact?.remark ?? ''),
			confidential: fb.control<boolean>(contact?.confidential ?? false),
			keyword: fb.control<string>(contact?.keyword ?? '', [Validators.required]),
			contactType: fb.control<ContactType | undefined>(contact?.contactType, [Validators.required]),
			contactedAt: fb.control<Date | undefined>(contact?.contactedAt, [
				Validators.required,
				minDateValidator(this.minDate()),
				maxDateValidator(this.maxDate()),
			]),
			participants: fb.control<SearchResultItem[]>(participants),
			documents: fb.control<DocumentFile[]>(contact?.documents ?? [], [
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.allowedFileTypes),
			]),
		});
	}
}
